import { trackError } from '@billy/track-error'

import { NumberValue } from '../../../../utils'
import { MAX_DEPOSIT_INVOICE_PERCENT } from '../../constants/maxDepositInvoicePercent'
import { DepositInvoiceAmountType } from '../../enums/depositInvoiceAmountType'
import type { DepositInvoiceCalculationRemaining } from '../../types/invoiceDeposit/depositInvoiceCalculationRemaining'
import type { InvoiceLine } from '../../types/invoices/invoiceLine'
import { InvoiceDepositCalculator } from '../invoiceDepositCalculator'
import { groupQuoteRowLinesByVatRate } from './groupQuoteRowLinesByVatRate'

interface CalculateDepositInvoiceRemainingOptions {
  currency: string
  depositAmount: number
  depositType: DepositInvoiceAmountType
  quoteAmount: number
  quoteLines?: InvoiceLine[]
}

const isCalculableDepositAmount = (depositAmount: number, depositType: DepositInvoiceAmountType): boolean => {
  if (!Number.isFinite(depositAmount) || depositAmount < 0) {
    return false
  }

  return depositType !== DepositInvoiceAmountType.Percentage || depositAmount <= MAX_DEPOSIT_INVOICE_PERCENT
}

export const calculateDepositInvoiceRemaining = (
  options: CalculateDepositInvoiceRemainingOptions,
): DepositInvoiceCalculationRemaining => {
  const { currency, depositAmount, depositType, quoteAmount, quoteLines } = options
  const vatRateGroups = groupQuoteRowLinesByVatRate(quoteLines)

  try {
    const result = InvoiceDepositCalculator.calculate({
      baseNetTotal: quoteAmount,
      currency,
      // The form can hold an empty or out-of-range value while the user is typing
      depositAmount: isCalculableDepositAmount(depositAmount, depositType) ? depositAmount : 0,
      depositType,
      // The package rejects negative groups; a VAT group that nets to a credit takes no share of the deposit
      groups: vatRateGroups.map((group) => ({ netTotal: Math.max(group.netTotal, 0), vatRate: group.vatRatePercent })),
    })

    return {
      remainingBalance: result.remainingNetAmount,
      vatLines: vatRateGroups.map((group, index) => ({
        depositAmountForVatItem: result.groups[index].netAmount,
        depositGrossAmountForVatItem: result.groups[index].grossAmount,
        percentage: new NumberValue(group.vatRatePercent).multiply(100).getValue(),
        vatRateId: group.vatRateId,
      })),
    }
  } catch (error) {
    trackError(error)

    return { remainingBalance: quoteAmount, vatLines: [] }
  }
}
